/**
 * Professional Schedule Service
 * API integration for Professional Schedule CRUD operations
 *
 * @version 2025.1.0
 * @description Serviço de integração com API de Agendas de Profissionais
 */
import apiClient from './apiClient';
import {
  ProfessionalSchedule,
  ProfessionalScheduleFilters,
  ProfessionalScheduleFormData,
  ProfessionalScheduleListResponse,
  BulkScheduleFormData,
  ScheduleReplicationData,
  SlotGenerationOptions,
  SlotGenerationResult,
  ProfessionalForSchedule,
  ClinicForSchedule,
  SLOT_DURATIONS,
  WEEK_DAY_NAMES,
  WeekDay,
} from '../types/professionalSchedule';

// Re-export types for convenience
export * from '../types/professionalSchedule';

export interface ProfessionalScheduleListParams extends ProfessionalScheduleFilters {
  page?: number;
  limit?: number;
}

class ProfessionalScheduleService {
  private baseUrl = '/professional-schedules';

  /**
   * Get paginated list of professional schedules
   */
  async getSchedules(params: ProfessionalScheduleListParams = {}): Promise<ProfessionalScheduleListResponse> {
    const { page = 1, limit = 50, ...filters } = params;

    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    if (filters.professionalId) queryParams.append('professionalId', filters.professionalId.toString());
    if (filters.clinicId) queryParams.append('clinicId', filters.clinicId.toString());
    if (filters.weekDay !== undefined) queryParams.append('weekDay', filters.weekDay.toString());
    if (filters.isActive !== undefined) queryParams.append('isActive', filters.isActive.toString());
    if (filters.sortBy) queryParams.append('sortBy', filters.sortBy);
    if (filters.sortOrder) queryParams.append('sortOrder', filters.sortOrder);

    // httpClient.get already unwraps axios response.data, so 'response' IS the backend JSON body:
    // { message, data: [...slots...], pagination: { total, page, limit, totalPages } }
    const response = await apiClient.get<ProfessionalSchedule[]>(`${this.baseUrl}?${queryParams.toString()}`);

    // response.data = the slots array, response.pagination lives at the top-level response
    const fullResponse = response as unknown as { data?: ProfessionalSchedule[]; pagination?: { total: number; page: number; limit: number; totalPages: number } };
    const schedules = Array.isArray(fullResponse.data) ? fullResponse.data : (fullResponse.data ?? []);
    const pagination = fullResponse.pagination;

    return {
      schedules,
      total: pagination?.total ?? schedules.length,
      page: pagination?.page ?? page,
      limit: pagination?.limit ?? limit,
      totalPages: pagination?.totalPages ?? Math.ceil((pagination?.total ?? schedules.length) / limit),
    };
  }

  /**
   * Get schedule by ID
   */
  async getScheduleById(id: string | number): Promise<ProfessionalSchedule> {
    const response = await apiClient.get(`${this.baseUrl}/${id}`);
    return response.data.data || response.data;
  }

  /**
   * Get schedules by professional ID
   */
  async getByProfessional(professionalId: number): Promise<ProfessionalSchedule[]> {
    const response = await apiClient.get(`${this.baseUrl}/professional/${professionalId}`);
    return response.data.data ?? [];
  }

  /**
   * Get schedules by clinic ID
   */
  async getByClinic(clinicId: number): Promise<ProfessionalSchedule[]> {
    const response = await apiClient.get(`${this.baseUrl}/clinic/${clinicId}`);
    return response.data.data ?? [];
  }

  /**
   * Create new schedule
   */
  async createSchedule(data: ProfessionalScheduleFormData): Promise<ProfessionalSchedule> {
    const response = await apiClient.post(this.baseUrl, data);
    return response.data.data || response.data;
  }

  /**
   * Bulk create schedules (for wizard)
   */
  async bulkCreate(data: BulkScheduleFormData): Promise<ProfessionalSchedule[]> {
    const response = await apiClient.post(`${this.baseUrl}/bulk`, data);
    return response.data.data ?? [];
  }

  /**
   * Update schedule by ID
   */
  async updateSchedule(id: string | number, data: Partial<ProfessionalScheduleFormData>): Promise<ProfessionalSchedule> {
    const response = await apiClient.put(`${this.baseUrl}/${id}`, data);
    return response.data.data || response.data;
  }

  /**
   * Delete schedule by ID
   */
  async deleteSchedule(id: string | number): Promise<void> {
    await apiClient.delete(`${this.baseUrl}/${id}`);
  }

  /**
   * Replicate schedules from one professional to others
   */
  async replicateSchedules(data: ScheduleReplicationData): Promise<{ created: number; skipped: number }> {
    const response = await apiClient.post(`${this.baseUrl}/replicate`, data);
    return response.data.data || response.data;
  }

  /**
   * Generate slots from schedules
   */
  async generateSlots(options: SlotGenerationOptions): Promise<SlotGenerationResult & { message?: string }> {
    const response = await apiClient.post(`${this.baseUrl}/generate-slots`, options);
    // Return both the message and data from the response
    const data = response.data.data || response.data;
    return {
      ...data,
      message: response.data.message || response.message || `${data.generated || 0} slots gerados`
    };
  }

  /**
   * Get professionals for schedule selection
   * Note: We fetch ALL professionals (not filtered by company) so users can select any
   * The skipCompanyFilter option ensures all professionals are returned regardless of
   * the selected company in the header
   */
  async getProfessionals(clinicId?: number): Promise<ProfessionalForSchedule[]> {
    const url = clinicId
      ? `/professionals?clinicId=${clinicId}&isActive=true`
      : '/professionals?isActive=true';
    const response = await apiClient.get(url, { skipCompanyFilter: true });
    // httpClient.get returns response.data, so structure is { data: { professionals: [...] } }
    const professionals = response.data?.professionals ?? response.data ?? [];
    return professionals.map((p: Record<string, unknown>) => {
      const meta = p['meta'] as Record<string, unknown> | undefined;
      const durationRaw = meta?.['defaultAppointmentDuration'];
      const defaultAppointmentDuration = durationRaw ? Number(durationRaw) : undefined;
      return {
        id: Number(p['id']),
        name: p['name'] as string,
        specialty: meta?.['specialty'] as string | undefined,
        isActive: (p['situacao'] as string) === 'Ativo',
        defaultAppointmentDuration: defaultAppointmentDuration && !isNaN(defaultAppointmentDuration) ? defaultAppointmentDuration : undefined,
      };
    });
  }

  /**
   * Get clinics for schedule selection
   * Optionally filtered by professional (returns only clinics where the professional has active bindings)
   */
  async getClinics(professionalId?: number): Promise<ClinicForSchedule[]> {
    if (professionalId) {
      // Fetch providers linked to this professional
      const bindingsResponse = await apiClient.get(`/providers/by-professional/${professionalId}`, { skipCompanyFilter: true });
      const bindings = bindingsResponse.data?.data ?? bindingsResponse.data ?? [];
      if (!Array.isArray(bindings) || bindings.length === 0) return [];

      // Fetch all active providers and filter by bound IDs
      const response = await apiClient.get('/providers?status=ATIVO', { skipCompanyFilter: true });
      const providers = response.data?.providers ?? response.data ?? [];
      const boundProviderIds = new Set(bindings.map((b: Record<string, unknown>) => Number(b['entityId'])));

      return providers
        .filter((p: Record<string, unknown>) => boundProviderIds.has(Number(p['id'])))
        .map((p: Record<string, unknown>) => ({
          id: Number(p['id']),
          tradeName: (p['tradeName'] as string) || (p['legalName'] as string),
          legalName: p['legalName'] as string,
          defaultSlotDuration: (p['defaultSlotDuration'] as number) || 30,
          serviceType: p['serviceType'] as string | undefined,
          city: p['city'] as string | undefined,
          state: p['state'] as string | undefined,
        }));
    }

    const response = await apiClient.get('/providers?status=ATIVO', { skipCompanyFilter: true });
    const providers = response.data?.providers ?? response.data ?? [];
    return providers.map((p: Record<string, unknown>) => ({
      id: Number(p['id']),
      tradeName: (p['tradeName'] as string) || (p['legalName'] as string),
      legalName: p['legalName'] as string,
      defaultSlotDuration: (p['defaultSlotDuration'] as number) || 30,
      serviceType: p['serviceType'] as string | undefined,
      city: p['city'] as string | undefined,
      state: p['state'] as string | undefined,
    }));
  }

  /**
   * Get slot duration options for select dropdowns
   */
  getSlotDurationOptions(): Array<{ value: number; label: string }> {
    return SLOT_DURATIONS.map(duration => ({
      value: duration,
      label: duration < 60 ? `${duration} minutos` : `${duration / 60}h${duration % 60 > 0 ? ` ${duration % 60}min` : ''}`,
    }));
  }

  /**
   * Get week day options for select dropdowns
   */
  getWeekDayOptions(): Array<{ value: WeekDay; label: string }> {
    return Object.entries(WEEK_DAY_NAMES).map(([key, label]) => ({
      value: parseInt(key) as WeekDay,
      label,
    }));
  }

  /**
   * Health check
   */
  async healthCheck(): Promise<{ status: string; timestamp: string }> {
    const response = await apiClient.get(`${this.baseUrl}/health`);
    return response.data.data || response.data;
  }

  // ============================================================================
  // SCHEDULE SLOT BLOCKING OPERATIONS
  // ============================================================================

  /**
   * Block slots for a period
   */
  async blockSlotsInPeriod(data: {
    professionalId: number;
    startDate: string;
    endDate: string;
    reason: string;
    clinicId?: number;
  }): Promise<{ blockedCount: number; affectedAppointments: number }> {
    const response = await apiClient.post('/schedule-slots/block-period', data);
    return response.data.data || response.data;
  }

  /**
   * Unblock slots for a period
   */
  async unblockSlotsInPeriod(data: {
    professionalId: number;
    startDate: string;
    endDate: string;
    clinicId?: number;
  }): Promise<{ unblockedCount: number }> {
    const response = await apiClient.post('/schedule-slots/unblock-period', data);
    return response.data.data || response.data;
  }

  /**
   * Count slots in a period (for preview before blocking)
   */
  async countSlotsInPeriod(params: {
    professionalId: number;
    startDate: string;
    endDate: string;
    clinicId?: number;
  }): Promise<number> {
    const queryParams = new URLSearchParams({
      professionalId: params.professionalId.toString(),
      startDate: params.startDate,
      endDate: params.endDate,
    });
    if (params.clinicId) {
      queryParams.append('clinicId', params.clinicId.toString());
    }
    const response = await apiClient.get(`/schedule-slots/count-in-period?${queryParams.toString()}`);
    // Handle different response structures
    const data = response.data || response;
    return (data as { count?: number }).count ?? 0;
  }
}

// Export singleton instance
export const professionalScheduleService = new ProfessionalScheduleService();
export default professionalScheduleService;
